/**
 * The rail (13-09, mock `.rail`): "Done today" with Reopen, and "In others' hands".
 *
 * Done today is this session's committed actions plus today's completed rows of mine. Reopen
 * builds the `reopen` verb and commits it through the outbox, so it is audited (D-13-04-4: same
 * Melbourne day only). Who completed a loaded row comes from its audit trail (ISS-188); a refusal
 * is shown under the item. With the toast's 6 s undo this is the WCAG 2.2.1 way back.
 */

import { forwardRef } from 'react';
import type { DoneItem, OtherItem } from '../../lib/todayQueue';
import type { ActionRow } from '../../types/actionRegister';
import styles from './today.module.css';

const TIME = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'Australia/Melbourne',
  hour: 'numeric',
  minute: '2-digit',
});

function timeOf(iso: string | null): string {
  const t = Date.parse(iso ?? '');
  return Number.isNaN(t) ? '' : TIME.format(t).replace(/\s/g, ' ').toLowerCase();
}

export interface DoneTodayProps {
  done: DoneItem[];
  others: OtherItem[];
  notes: Readonly<Record<number, string>>;
  onReopen: (item: DoneItem) => void;
  /** "View context" on a regulatory call Oversight makes (13-10): the panel, read only. */
  onViewContext?: (row: ActionRow) => void;
  /** Ids added this session, for the settle-in motion. */
  fresh: ReadonlySet<number>;
}

const DoneToday = forwardRef<HTMLHeadingElement, DoneTodayProps>(function DoneToday(
  { done, others, notes, onReopen, onViewContext, fresh },
  doneHeading,
) {
  return (
    <aside className={styles.rail} aria-label="Today, at a glance">
      <section aria-labelledby="done-today">
        <h2 id="done-today" ref={doneHeading} tabIndex={-1}>
          Done today
        </h2>
        <ul>
          {done.length === 0 && <li className={styles.empty}>Things you finish show up here.</li>}
          {done.map((d) => (
            <li key={`${d.id}-${d.verb}`} className={fresh.has(d.id) ? styles.fresh : undefined}>
              <div className={styles.railT}>{d.title}</div>
              <div className={styles.railM}>
                <span>
                  {d.verb} {timeOf(d.at)}
                </span>
                {d.row.status === 'Done' && (
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.quiet} ${styles.sm}`}
                    onClick={() => onReopen(d)}
                    aria-label={`Reopen ${d.title}`}
                  >
                    Reopen
                  </button>
                )}
              </div>
              {notes[d.id] && (
                <p className={styles.railNote} role="status">
                  {notes[d.id]}
                </p>
              )}
            </li>
          ))}
        </ul>
      </section>
      <section aria-labelledby="others-hands">
        <h2 id="others-hands">In others&rsquo; hands</h2>
        <ul>
          {others.length === 0 && <li className={styles.empty}>Nothing waiting on anyone else.</li>}
          {others.map((o) => (
            <li key={`o-${o.id}`}>
              <div className={styles.railT}>{o.title}</div>
              <div className={styles.railM}>
                <span>{o.text}</span>
                {o.context && onViewContext && (
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.quiet} ${styles.sm}`}
                    onClick={() => o.context && onViewContext(o.context)}
                    aria-label={`View context: ${o.title}`}
                  >
                    View context
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </aside>
  );
});

export default DoneToday;
